import React, { useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { Modal } from "antd";
import { formatPostDate } from "../../../../Utils/StayUpToDate/RenderComponent";
import {
  parseNewsletterDescriptionHtml,
  resolveNewsletterTitle,
} from "../../../../Utils/newsletterDescription";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import { useTemplateAnalyticsOptional } from "../TemplateAnalyticsContext";
import { useTemplateSectionInstance } from "../templateData";

export interface TemplateNewsletterFeedTileProps {
  post: Record<string, unknown>;
}

/**
 * Newsletter grid tile for website template feeds — same card + Shop/View button
 * chrome as other platforms; dark media tile with centered white title + date.
 */
export function TemplateNewsletterFeedTile({ post }: TemplateNewsletterFeedTileProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const analytics = useTemplateAnalyticsOptional();
  const sectionPath = useTemplateSectionInstance();

  const caption = useMemo(() => resolveNewsletterTitle(post), [post]);
  const postDate = post.post_date as string | undefined;
  const mediaUrl = String(post.media_url ?? "").trim();
  const descriptionHtml = useMemo(
    () => parseNewsletterDescriptionHtml(post.metadata),
    [post.metadata],
  );

  const modalTitle = postDate
    ? `${formatPostDate(postDate)} Newsletter`
    : "Newsletter";

  const openModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const button = "See Entire Newsletter";
    const title = String(caption || "").trim();
    const sectionId =
      String(sectionPath || "").split("/").pop()?.split(".").pop() || "newsletter";
    analytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.SECTION_CLICK, {
      sectionId,
      sectionType: "Newsletter",
      entityType: "section",
      entityId: String(post.id || post.post_id || title || button),
      entityLabel: title ? `${button} · ${title}` : button,
    });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const modalBodyStyle = useMemo(
    () => ({
      width: "100%",
      height: "100%",
      overflow: "auto" as const,
    }),
    [],
  );

  const mediaClass = [
    "mte-tpl-nl-feed-media",
    "newsletter-media-carousel",
    "card-image-individual",
    "no-media-url",
    mediaUrl ? "mte-tpl-nl-feed-media--has-img" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card style={{ border: "none" }}>
      <div
        className="creator-feed-image-wrapper-links"
        style={{
          textAlign: "center",
          height: "100%",
          borderRadius: "1rem",
          position: "relative",
        }}
      >
        <div className={mediaClass}>
          {mediaUrl ? (
            <img src={mediaUrl} alt="" className="mte-tpl-nl-feed-media-bg" />
          ) : null}
          <div className="mte-tpl-nl-feed-card-head">
            <p className="mte-tpl-nl-feed-title newsletter-label">{caption}</p>
            {postDate ? (
              <p className="mte-tpl-nl-feed-date date">{formatPostDate(postDate)}</p>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="template-shop-btn-links-individual custom-button-with-hover"
          onClick={openModal}
        >
          See Entire Newsletter
        </button>
        <Modal
          className="mte-tpl-nl-feed-modal"
          styles={{
            body: { overflowY: "auto", maxHeight: "calc(100vh - 250px)" },
          }}
          title={modalTitle}
          open={modalOpen}
          footer={null}
          onCancel={closeModal}
          destroyOnClose
          getContainer={() => document.body}
          zIndex={10050}
        >
          {descriptionHtml ? (
            <div
              className="mte-tpl-nl-feed-tile-modal-body"
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              style={modalBodyStyle}
            />
          ) : (
            <p className="mte-tpl-nl-feed-tile-modal-empty">
              Newsletter content is not available for this post.
            </p>
          )}
        </Modal>
      </div>
    </Card>
  );
}
